<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-start justify-center pt-10 bg-black/50"
    @click="handleClickOutside"
  >
    <div
      class="relative w-full max-w-lg h-auto max-h-[90dvh] p-4 overflow-y-auto bg-white rounded-xl shadow-lg sm:p-8"
    >
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-xl font-bold">{{ title }}</h2>
        <button
          type="button"
          aria-label="Close"
          @click="closeModal"
          class="text-gray-500 transition hover:text-gray-700"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Current Image Preview -->
      <div v-if="currentImageUrl && !form.image" class="mb-4">
        <p class="mb-2 text-sm font-medium text-gray-700">Gambar saat ini:</p>
        <div
          class="relative flex items-center justify-center w-full overflow-hidden bg-gray-100 border rounded"
          style="aspect-ratio: 16/9"
        >
          <img
            :src="currentImageUrl"
            :alt="`Current image ${fileIndex + 1}`"
            class="object-cover w-full h-full rounded"
          />
        </div>
        <p class="mt-1 text-xs text-gray-500">
          File: {{ currentImageUrl.split("/").pop() }}
        </p>
      </div>

      <!-- New Image Preview -->
      <div v-if="form.image" class="mb-4">
        <p class="mb-2 text-sm font-medium text-gray-700">Gambar baru:</p>
        <div
          class="relative flex items-center justify-center w-full mb-2 overflow-hidden bg-gray-100 border rounded"
          style="aspect-ratio: 16/9"
        >
          <img
            :src="createObjectURL(form.image)"
            alt="Preview"
            class="object-cover w-full h-full rounded"
          />
        </div>
        <p class="text-xs text-gray-500">File baru: {{ form.image?.name }}</p>
      </div>

      <p class="mb-4 text-sm text-gray-600">
        Pilih file gambar baru untuk mengganti gambar pada posisi ke-{{
          fileIndex + 1
        }}
        dalam gallery "{{ gallery.title }}".
      </p>

      <form class="space-y-6" @submit.prevent="submit">
        <div>
          <InputLabel
            for="image"
            value="File Gambar"
            class="block text-sm font-medium text-gray-700"
          />
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            @change="handleFileChange"
            class="block w-full mt-1 text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer focus:ring-indigo-500 focus:border-indigo-500"
          />
          <p class="mt-1 text-xs text-gray-500">
            Format yang didukung: JPG, JPEG, PNG, GIF (maksimal 2MB)
          </p>
          <progress
            v-if="form.progress"
            :value="form.progress.percentage"
            max="100"
            class="w-full mt-2"
          >
            {{ form.progress.percentage }}%
          </progress>
          <InputError :message="form.errors.image" class="mt-2" />
        </div>
        <div class="flex space-x-3">
          <PrimaryBtn :disabled="form.processing || !form.image">
            <i class="fa-solid fa-floppy-disk"></i> Simpan
          </PrimaryBtn>
          <button
            type="button"
            @click="closeModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Batal
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import InputError from "@/Components/InputError.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import InputLabel from "@/Components/InputLabel.vue";

import { defineProps, ref, computed, watch, nextTick, inject } from "vue";
import Swal from "sweetalert2";
import { useForm } from "@inertiajs/vue3";
const route = inject('route');

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  gallery: {
    type: Object,
    required: true,
  },
  fileIndex: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(["update:isOpen", "imageUpdated"]);

const form = useForm({
  idgallery: null,
  index: null,
  image: null,
});

const fileInput = ref(null);

const currentImageUrl = computed(() => {
  if (
    props.gallery &&
    Array.isArray(props.gallery.image_path) &&
    props.fileIndex >= 0
  ) {
    return props.gallery.image_path[props.fileIndex];
  }
  return null;
});

// Method to safely create object URL
const createObjectURL = (file) => {
  try {
    return window.URL.createObjectURL(file);
  } catch (error) {
    console.error("Error creating object URL:", error);
    return "";
  }
};

function handleFileChange(e) {
  const files = Array.from(e.target.files);

  // Validate file size (2MB max)
  const maxSize = 2 * 1024 * 1024; // 2MB in bytes
  const validFiles = files.filter((file) => {
    if (file.size > maxSize) {
      Swal.fire({
        title: "File terlalu besar",
        text: `File ${file.name} melebihi batas maksimal 2MB`,
        icon: "warning",
        confirmButtonText: "OK",
      });
      return false;
    }
    return true;
  });

  // Validate file type
  const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
  const validTypeFiles = validFiles.filter((file) => {
    if (!allowedTypes.includes(file.type)) {
      Swal.fire({
        title: "Format file tidak didukung",
        text: `File ${file.name} bukan format gambar yang valid`,
        icon: "warning",
        confirmButtonText: "OK",
      });
      return false;
    }
    return true;
  });

  // Set the first valid file to the image field
  form.image = validTypeFiles.length > 0 ? validTypeFiles[0] : null;
}

watch(
  () => props.gallery,
  (newGallery) => {
    if (newGallery) {
      form.idgallery = newGallery.idgallery;
      form.index = props.fileIndex;
      form.image = null; // Reset image
      nextTick(() => {
        if (fileInput.value) fileInput.value.value = "";
      });
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => props.fileIndex,
  (newIndex) => {
    form.index = newIndex;
  },
  { immediate: true }
);

const closeModal = () => {
  emit("update:isOpen", false);
  // Reset form
  form.image = null;
  if (fileInput.value) fileInput.value.value = "";
};

const handleClickOutside = (event) => {
  if (event.target === event.currentTarget) {
    closeModal();
  }
};

const submit = () => {
  if (!form.image) {
    Swal.fire({
      title: "Pilih file",
      text: "Silakan pilih file gambar terlebih dahulu",
      icon: "warning",
      confirmButtonText: "OK",
    });
    return;
  }

  form.post(route("gallery.updateImageByIndex", form.idgallery), {
    onSuccess: (page) => {
      // Tangkap flash message dari props
      const props = page.props;

      Swal.fire({
        title: "Berhasil!",
        text: "Data berhasil diperbarui",
        icon: "success",
        confirmButtonText: "OK",
      });

      form.reset();
      // Reset input file di DOM
      if (fileInput.value) fileInput.value.value = "";
      closeModal();

      // Emit event untuk refresh parent component jika diperlukan
      emit("imageUpdated");
    },
    onError: (errors) => {
      console.error("Upload errors:", errors);
      const errorMessage = errors.image || "Gagal mengupload gambar";
      Swal.fire({
        title: "Error!",
        text: Array.isArray(errorMessage) ? errorMessage[0] : errorMessage,
        icon: "error",
        confirmButtonText: "OK",
      });
    },
  });
};
</script>
